// @ts-nocheck — conflicto de tipos entre @types/testing-library__react y @types/react
import React, { useEffect, useState } from 'react'
import {
    Button,
    Form,
    Input,
    message,
    Popconfirm,
    Radio,
    Table,
    Tag,
    Tooltip,
} from 'antd'
import {
    ApiOutlined,
    BoxPlotOutlined,
    DownloadOutlined,
    SaveOutlined,
    SwapOutlined,
    SyncOutlined,
} from '@ant-design/icons'
import axios from 'axios'
import { serverUrl } from '../../Constants/env'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

// ─── Config ───────────────────────────────────────────────────────────────────

const ConfigSection = ({ headers, setMainLoading }: any) => {
    const [form] = Form.useForm()
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [configs, setConfigs] = useState<Record<string, any>>({})

    const fillForm = (cfg: any) => {
        form.setFieldsValue({
            name:        cfg?.name        || '',
            url:         cfg?.url         || '',
            username:    cfg?.username    || '',
            soap_server: cfg?.soap_server || '',
            own_code:    cfg?.own_code    || '',
            whs_code:    cfg?.whs_code    || '',
        })
        form.resetFields(['password'])
    }

    const loadConfigs = () =>
        axios
            .get(`${serverUrl}v3/eit/config`, { headers })
            .then((res) => {
                if (res.data.success && res.data.configs) {
                    const cfg = res.data.configs
                    setConfigs(cfg)
                    const activeMode = (Object.values(cfg) as any[]).find((c) => c.active)?.sync_mode || 'rest'
                    form.setFieldsValue({ sync_mode: activeMode })
                    fillForm(cfg[activeMode])
                }
            })

    useEffect(() => {
        loadConfigs()
            .catch(() => {})
            .finally(() => {
                setLoading(false)
                setMainLoading(false)
            })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleModeChange = (e: any) => {
        fillForm(configs[e.target.value])
    }

    const handleSave = async (values: any) => {
        setSaving(true)
        try {
            await axios.post(`${serverUrl}v3/eit/config`, values, { headers })
            message.success(`Modo ${values.sync_mode === 'soap' ? 'SOAP' : 'API REST'} guardado y activado`)
            await loadConfigs()
        } catch (err: any) {
            message.error(err?.response?.data?.message || 'Error al guardar')
        } finally {
            setSaving(false)
        }
    }

    return (
        <div style={{ maxWidth: 500, padding: 24 }}>
            <h2 style={{ marginBottom: 4 }}>Configuración EIT</h2>
            <p style={{ color: '#999', marginBottom: 24 }}>
                Guardar activa el modo seleccionado. Solo un modo puede estar activo a la vez.
            </p>
            <Form form={form} layout="vertical" onFinish={handleSave} initialValues={{ sync_mode: 'rest' }}>
                <Form.Item
                    name="sync_mode"
                    label={
                        <Tooltip title="Recepciones y ajustes siempre usan REST. Solo despachos cambian de fuente.">
                            Modo de despachos
                        </Tooltip>
                    }
                >
                    <Radio.Group buttonStyle="solid" onChange={handleModeChange}>
                        <Radio.Button value="rest">API REST (EIT)</Radio.Button>
                        <Radio.Button value="soap">SOAP (WMS-TEK)</Radio.Button>
                    </Radio.Group>
                </Form.Item>

                <Form.Item name="name" label="Nombre" rules={[{ required: true }]}>
                    <Input />
                </Form.Item>
                <Form.Item name="url" label="URL / Endpoint" rules={[{ required: true }]}>
                    <Input />
                </Form.Item>
                <Form.Item name="username" label="Usuario" rules={[{ required: true }]}>
                    <Input />
                </Form.Item>
                <Form.Item
                    name="password"
                    label="Contraseña"
                    extra="Requerida siempre al guardar (se cifra en BD)"
                    rules={[{ required: true }]}
                >
                    <Input.Password />
                </Form.Item>

                <Form.Item shouldUpdate noStyle>
                    {({ getFieldValue }) =>
                        getFieldValue('sync_mode') === 'soap' && (
                            <>
                                <Form.Item name="soap_server" label="Servidor WMS-TEK (SOAP)" rules={[{ required: true }]}>
                                    <Input placeholder="http://200.6.112.160/wmstekws" />
                                </Form.Item>
                                <Form.Item name="own_code" label="Own Code">
                                    <Input style={{ width: 160 }} />
                                </Form.Item>
                                <Form.Item name="whs_code" label="Whs Code">
                                    <Input style={{ width: 160 }} />
                                </Form.Item>
                            </>
                        )
                    }
                </Form.Item>

                <Form.Item>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={saving || loading}
                        icon={<SaveOutlined />}
                    >
                        Guardar y activar
                    </Button>
                </Form.Item>
            </Form>
        </div>
    )
}

// ─── Generic sync + paginated table ──────────────────────────────────────────

const SyncTable = ({
    syncUrl,
    listUrl,
    columns,
    expandedRowRender,
    rowKey,
    headers,
    emptyText,
}: any) => {
    const [data, setData] = useState([])
    const [loading, setLoading] = useState(true)
    const [syncing, setSyncing] = useState(false)
    const [pagination, setPagination] = useState({ current: 1, pageSize: 50, total: 0 })

    const fetchData = async (page = 1) => {
        setLoading(true)
        try {
            const res = await axios.get(`${serverUrl}${listUrl}`, {
                headers,
                params: { page },
            })
            const p = res.data
            setData(p.data || [])
            setPagination((prev) => ({ ...prev, current: p.current_page, total: p.total }))
        } catch {
            message.error('Error al cargar datos')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchData(1)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleSync = async () => {
        setSyncing(true)
        try {
            const res = await axios.get(`${serverUrl}${syncUrl}`, { headers })
            const count = res.data.count || 0
            message.success(
                count > 0 ? `${count} registro(s) nuevos sincronizados` : 'Sin registros nuevos'
            )
            if (count > 0) fetchData(1)
        } catch (err: any) {
            const msg = err?.response?.data?.message || 'Error al sincronizar'
            message.error(msg)
        } finally {
            setSyncing(false)
        }
    }

    return (
        <div style={{ padding: 24 }}>
            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-end' }}>
                <Popconfirm
                    title="¿Sincronizar ahora desde EIT?"
                    okText="Sincronizar"
                    cancelText="Cancelar"
                    onConfirm={handleSync}
                >
                    <Button type="primary" icon={<SyncOutlined />} loading={syncing}>
                        Sincronizar
                    </Button>
                </Popconfirm>
            </div>
            <Table
                dataSource={data}
                columns={columns}
                rowKey={rowKey}
                loading={loading}
                size="small"
                bordered
                pagination={{
                    ...pagination,
                    showSizeChanger: false,
                    onChange: (page) => fetchData(page),
                    showTotal: (total) => `${total} registros`,
                }}
                expandable={expandedRowRender ? { expandedRowRender } : undefined}
                locale={{ emptyText: emptyText || 'Sin registros' }}
                scroll={{ x: true }}
            />
        </div>
    )
}

// ─── Despachos ────────────────────────────────────────────────────────────────

const DispatchesSection = ({ headers }: any) => {
    const detailColumns = [
        { title: 'Línea', dataIndex: 'line_number', key: 'line_number', width: 60 },
        { title: 'SKU', dataIndex: 'item_code', key: 'item_code' },
        { title: 'CTG', dataIndex: 'ctg_code', key: 'ctg_code', width: 80 },
        { title: 'Cantidad', dataIndex: 'item_qty', key: 'item_qty', align: 'right' as const, width: 90 },
        { title: 'Lote', dataIndex: 'lot_number', key: 'lot_number' },
        { title: 'LPN', dataIndex: 'lpn_code', key: 'lpn_code' },
        {
            title: 'Precio',
            dataIndex: 'price',
            key: 'price',
            align: 'right' as const,
            width: 90,
        },
    ]

    const expandedRowRender = (record: any) => (
        <Table
            dataSource={record.details || []}
            columns={detailColumns}
            rowKey="id"
            size="small"
            pagination={false}
            locale={{ emptyText: 'Sin detalles' }}
        />
    )

    const columns = [
        { title: 'ID EIT', dataIndex: 'eit_id', key: 'eit_id', width: 80 },
        { title: 'Nro. Salida', dataIndex: 'outbound_number', key: 'outbound_number' },
        { title: 'Bodega', dataIndex: 'whs_code', key: 'whs_code', width: 80 },
        { title: 'Own', dataIndex: 'own_code', key: 'own_code', width: 80 },
        { title: 'Cliente', dataIndex: 'customer_code', key: 'customer_code' },
        { title: 'Transportista', dataIndex: 'carrier_name', key: 'carrier_name' },
        { title: 'Fecha', dataIndex: 'dispatch_date', key: 'dispatch_date', width: 110 },
        {
            title: 'Estado',
            dataIndex: 'state_interface',
            key: 'state_interface',
            width: 70,
            render: (v: string) => <Tag color={v === 'C' ? 'blue' : 'green'}>{v || '—'}</Tag>,
        },
    ]

    return (
        <SyncTable
            syncUrl="v3/eit/sync/dispatches"
            listUrl="v3/eit/dispatches"
            columns={columns}
            expandedRowRender={expandedRowRender}
            rowKey="id"
            headers={headers}
            emptyText="Sin despachos sincronizados"
        />
    )
}

// ─── Recepciones ──────────────────────────────────────────────────────────────

const ReceiptsSection = ({ headers }: any) => {
    const detailColumns = [
        { title: 'Línea', dataIndex: 'line_number', key: 'line_number', width: 60 },
        { title: 'SKU', dataIndex: 'item_code', key: 'item_code' },
        { title: 'CTG', dataIndex: 'ctg_code', key: 'ctg_code', width: 80 },
        { title: 'Cantidad', dataIndex: 'item_qty', key: 'item_qty', align: 'right' as const, width: 90 },
        { title: 'Lote', dataIndex: 'lot_number', key: 'lot_number' },
        { title: 'LPN', dataIndex: 'lpn_code', key: 'lpn_code' },
        {
            title: 'Precio',
            dataIndex: 'price',
            key: 'price',
            align: 'right' as const,
            width: 90,
        },
    ]

    const expandedRowRender = (record: any) => (
        <Table
            dataSource={record.details || []}
            columns={detailColumns}
            rowKey="id"
            size="small"
            pagination={false}
            locale={{ emptyText: 'Sin detalles' }}
        />
    )

    const columns = [
        { title: 'ID EIT', dataIndex: 'eit_id', key: 'eit_id', width: 80 },
        { title: 'Nro. Ingreso', dataIndex: 'inbound_number', key: 'inbound_number' },
        { title: 'Bodega', dataIndex: 'whs_code', key: 'whs_code', width: 80 },
        { title: 'Own', dataIndex: 'own_code', key: 'own_code', width: 80 },
        { title: 'Proveedor', dataIndex: 'vendor_code', key: 'vendor_code' },
        { title: 'Tipo', dataIndex: 'receipt_type_code', key: 'receipt_type_code', width: 70 },
        { title: 'Fecha', dataIndex: 'receipt_date', key: 'receipt_date', width: 110 },
        {
            title: 'Estado',
            dataIndex: 'state_interface',
            key: 'state_interface',
            width: 70,
            render: (v: string) => <Tag color={v === 'C' ? 'blue' : 'green'}>{v || '—'}</Tag>,
        },
    ]

    return (
        <SyncTable
            syncUrl="v3/eit/sync/receipts"
            listUrl="v3/eit/receipts"
            columns={columns}
            expandedRowRender={expandedRowRender}
            rowKey="id"
            headers={headers}
            emptyText="Sin recepciones sincronizadas"
        />
    )
}

// ─── Ajustes de inventario ────────────────────────────────────────────────────

const MovementsSection = ({ headers }: any) => {
    const columns = [
        { title: 'ID EIT', dataIndex: 'eit_id', key: 'eit_id', width: 80 },
        { title: 'Tipo Mov.', dataIndex: 'id_movement_type', key: 'id_movement_type', width: 90 },
        { title: 'Bodega', dataIndex: 'whs_code', key: 'whs_code', width: 80 },
        { title: 'Own', dataIndex: 'own_code', key: 'own_code', width: 80 },
        { title: 'SKU', dataIndex: 'item_code', key: 'item_code' },
        { title: 'CTG', dataIndex: 'ctg_code', key: 'ctg_code', width: 80 },
        {
            title: 'Cantidad',
            dataIndex: 'item_qty',
            key: 'item_qty',
            align: 'right' as const,
            width: 90,
            render: (v: number) => (
                <span style={{ color: v < 0 ? '#ff4d4f' : 'inherit' }}>{v}</span>
            ),
        },
        { title: 'Motivo', dataIndex: 'reason_code', key: 'reason_code', width: 90 },
        { title: 'Documento', dataIndex: 'document_number', key: 'document_number' },
        { title: 'Fecha', dataIndex: 'adjust_date', key: 'adjust_date', width: 110 },
        {
            title: 'Estado',
            dataIndex: 'state_interface',
            key: 'state_interface',
            width: 70,
            render: (v: string) => <Tag color={v === 'C' ? 'blue' : 'green'}>{v || '—'}</Tag>,
        },
    ]

    return (
        <SyncTable
            syncUrl="v3/eit/sync/movements"
            listUrl="v3/eit/movements"
            columns={columns}
            rowKey="id"
            headers={headers}
            emptyText="Sin ajustes sincronizados"
        />
    )
}

// ─── Main ─────────────────────────────────────────────────────────────────────

const EITIndex = ({ exposeSubMenu, setMainLoading }: any) => {
    const { token } = useSelector((state: any) => state)
    const { pathname } = useLocation()
    const section = pathname.split('/')[2] || 'config'

    useEffect(() => {
        exposeSubMenu([
            { key: 'config',     name: 'Configuración', icon: <ApiOutlined /> },
            { key: 'dispatches', name: 'Despachos',     icon: <DownloadOutlined /> },
            { key: 'receipts',   name: 'Recepciones',   icon: <BoxPlotOutlined /> },
            { key: 'movements',  name: 'Ajustes',       icon: <SwapOutlined /> },
        ])
        // config maneja su propio setMainLoading; el resto lo apaga de inmediato
        if (section !== 'config') setMainLoading(false)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
    }

    if (section === 'dispatches') return <DispatchesSection headers={headers} />
    if (section === 'receipts')   return <ReceiptsSection   headers={headers} />
    if (section === 'movements')  return <MovementsSection  headers={headers} />
    return <ConfigSection headers={headers} setMainLoading={setMainLoading} />
}

export default EITIndex
